import { useQuery } from '@tanstack/react-query';
import { api, type LeaderboardRow } from '../lib/api';
import { credits } from '../lib/format';
import { useSession } from '../lib/useSession';

/**
 * Who has actually forecast well.
 *
 * Ranked on realised profit rather than balance, which matters more than it
 * sounds: a balance includes the signup grant and any credits an operator has
 * handed out, and neither is skill. Profit is computed only from markets that
 * have settled, and those entries are the only ones carrying a market id — so
 * a grant is invisible to this table by construction, not by a filter someone
 * has to remember to maintain.
 *
 * Three exclusions, each closing a way to appear here without forecasting:
 * unverified addresses, suspended accounts, and anyone with fewer than three
 * settled markets. The last one is the honest part — one lucky call is not a
 * track record, and a table that ranked it would be inviting people to open
 * accounts until one of them got lucky.
 */
export function Leaderboard() {
  const signedIn = useSession();
  const board = useQuery({ queryKey: ['leaderboard'], queryFn: api.leaderboard });

  const rows = board.data?.top ?? [];
  const mine = board.data?.me ?? null;
  const inTable = mine !== null && rows.some((r) => r.userId === mine.userId);

  return (
    <div className="stack">
      <h1 className="question">Leaderboard</h1>

      <div className="card">
        <h2>Ranked by realised profit</h2>
        {board.isLoading ? (
          <div className="empty-state">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            Nobody qualifies yet. Traders appear here once they have{' '}
            {board.data?.minimumSettledMarkets ?? 3} settled markets behind them.
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 44 }}>#</th>
                <th>Trader</th>
                <th>Settled</th>
                <th>Record</th>
                <th>Right</th>
                <th>Profit</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Row key={r.userId} row={r} highlight={mine?.userId === r.userId} />
              ))}
            </tbody>
          </table>
        )}

        {/* Somebody outside the top 50 still gets to see where they stand. */}
        {mine && !inTable && (
          <>
            <div style={{ borderTop: '1px solid var(--border)', margin: '12px 0 0' }} />
            <table className="data">
              <tbody>
                <Row row={mine} highlight />
              </tbody>
            </table>
          </>
        )}

        <p style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.55, marginTop: 14, marginBottom: 0 }}>
          Profit counts only markets that have resolved. Starting credits and any adjustment by an
          administrator are excluded, so the table measures forecasting rather than balance.
          {signedIn && !mine && (
            <>
              {' '}
              You will appear once {board.data?.minimumSettledMarkets ?? 3} of the markets you traded
              have settled.
            </>
          )}
        </p>
      </div>
    </div>
  );
}

function Row({ row, highlight }: { row: LeaderboardRow; highlight?: boolean }) {
  const value = BigInt(row.realisedPnl);
  return (
    <tr style={highlight ? { background: 'var(--surface-2)' } : undefined}>
      <td className="num" style={{ color: 'var(--muted)' }}>{row.rank}</td>
      <td>
        {row.displayName}
        {highlight && <span className="badge" style={{ marginLeft: 8 }}>you</span>}
      </td>
      <td className="num">{row.marketsSettled}</td>
      <td className="num muted">{row.wins}–{row.losses}</td>
      <td className="num">{row.winRate === null ? '—' : `${row.winRate}%`}</td>
      <td
        className="num"
        style={{ color: value > 0n ? 'var(--good)' : value < 0n ? 'var(--critical)' : undefined }}
      >
        {value > 0n ? '+' : value < 0n ? '−' : ''}
        {credits(value < 0n ? -value : value)}
      </td>
    </tr>
  );
}
